/**
 * Your crew.
 *
 * Built to the dark Crew reference: an integrated cover carrying the crew's
 * mark, name, tag, member and online counts and Invite member; Overview /
 * Members / Events; compact member rows; the voice lounge and crew level side
 * by side; a compact events block; About; and crew chat docked beside it all
 * (see CrewChat for its three sizes). The same components run in the light
 * theme - every colour here is a theme token.
 *
 * Member rows carry the one set of member actions three ways: the visible
 * options button, right-click anywhere on the row that is not itself a
 * control, and the keyboard (the context-menu key or Shift+F10 on a focused
 * row). The actions shown follow your role and theirs; the server enforces
 * the same rules.
 *
 * Somebody with no crew gets the two ways in instead; ?find=1 opens the
 * directory.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/lib/api';
import { useSession } from '@/stores/session';
import { useVoice } from '@/stores/voice';
import { useCrewChat } from '@/stores/crewChat';
import { cn } from '@/lib/cn';
import { usePaged, ShowMore } from '@/components/Paged';
import { go, useRoute } from '@/lib/hash';
import { Icon } from '@/icons/Icon';
import { Rsvp } from '@/components/Rsvp';
import { CrewHistory, CrewWelcome, type PastEvent } from '@/components/CrewHistory';
import { Announce, Challenges } from '@/components/CrewExtras';
import { WhenFree } from '@/components/WhenFree';
import { Tournaments } from '@/components/Tournaments';
import { downloadIcs } from '@/lib/ics';
import { Avatar } from '@/components/Avatar';
import { UserName, openUserCard } from '@/components/user';
import { Button, Empty, Field, IconButton, Input, Modal, Textarea, useConfirm, useToast } from '@/components/ui';
import { MenuPanel, type MenuItem } from '@/components/Menu';
import { CrewTag, type Crew, type CrewEvent, type CrewMember } from '@/components/Crew';
import { CrewWorkspace } from '@/components/CrewWorkspace';
import { FindCrew } from '@/components/FindCrew';
import { CreateCrewPanel } from '@/components/CreateCrew';
import { CrewBanner, CrewMark, crewColor } from '@/components/CrewArt';
import { CrewChat } from '@/components/CrewChat';
import { InviteToParty } from '@/components/userMenu';
import { Select } from '@/components/Select';
import { Tooltip } from '@/components/Tooltip';
import type { PublicUser } from '@/lib/types';

type Tab = 'overview' | 'members' | 'events';
type Act = (path: string, body?: unknown, note?: string, method?: 'POST' | 'DELETE') => Promise<boolean>;

const JOIN_TEXT: Record<Crew['settings']['join'], [string, string]> = {
  open: ['Public crew', 'Anyone can join'],
  request: ['Request to join', 'The host or a co-host approves each request'],
  invite: ['Invite only', 'Members invite new people in']
};

const ROLE_LABEL: Record<CrewMember['role'], string> = { owner: 'Host', officer: 'Co-host', member: 'Member' };

/* The edge is an inset line rather than a border, so the rounded corners stay smooth at any display scale. */
const CARD = 'pugo-panel rounded-[calc(20px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]';

export function CrewsView() {
  const me = useSession((s) => s.me);
  const tag = me?.crew?.tag ?? null;
  const routeQ = useRoute().query as Record<string, string | undefined>;

  const [finding, setFinding] = useState(routeQ.find === '1');
  const [making, setMaking] = useState(false);
  useEffect(() => { if (routeQ.find === '1') setFinding(true); }, [routeQ.find]);

  const q = useQuery({
    queryKey: ['crew', tag],
    queryFn: () => api.get<{ crew: Crew }>('/api/crews/' + encodeURIComponent(tag!)),
    enabled: !!tag
  });

  return (
    <div className="relative flex min-h-0 flex-1">
      {tag ? (
        q.data?.crew ? (
          <CrewScreen crew={q.data.crew} tag={tag} />
        ) : q.isError ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-[640px] px-4 py-10">
              <Empty
                role="alert"
                title="Your crew could not be loaded"
                note="You are still in it; the page just did not arrive."
                actions={<Button kind="outline" disabled={q.isFetching} onClick={() => void q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</Button>}
              />
            </div>
          </div>
        ) : (
          <CrewSkeleton />
        )
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[860px] px-4 py-6 sm:px-6">
            <NoCrew onFind={() => setFinding(true)} onMake={() => setMaking(true)} />
          </div>
        </div>
      )}

      {finding ? <FindCrew onClose={() => { setFinding(false); if (routeQ.find) go('crews'); }} /> : null}
      {making ? <CreateCrewPanel onClose={() => setMaking(false)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------ the crew */

/** The time, refreshed every so often - enough for events to start and finish on screen. */
function useNow(every: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), every);
    return () => window.clearInterval(t);
  }, [every]);
  return now;
}

/** When an event is over. Older data carries no end, so two hours. */
function eventEnd(e: CrewEvent) {
  return e.endsAt ?? e.startsAt + 2 * 60 * 60 * 1000;
}

function CrewScreen({ crew: loaded, tag }: { crew: Crew; tag: string }) {
  const now = useNow(20_000);
  /* A finished event goes the moment it ends, without waiting for a refresh. */
  const crew = useMemo(
    () => ({ ...loaded, events: loaded.events.filter((e) => eventEnd(e) > now).map((e) => ({ ...e, live: e.startsAt <= now })) }),
    [loaded, now]
  );
  const covering = useCrewChat((s) => s.covering);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const chatMode = useCrewChat((s) => s.mode);
  const setChatMode = useCrewChat((s) => s.setMode);
  const unread = useCrewChat((s) => s.unread['cc:' + crew.id] ?? 0);

  const [tab, setTab] = useState<Tab>('overview');
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [about, setAbout] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [planning, setPlanning] = useState(false);
  /* An evening being run again, carrying what it was. */
  const [again, setAgain] = useState<PastEvent | null>(null);
  const [announcing, setAnnouncing] = useState(false);
  const [partyInvite, setPartyInvite] = useState<PublicUser | null>(null);
  const [moreAt, setMoreAt] = useState<{ x: number; y: number } | null>(null);

  const mine = crew.myRole;
  const canManage = mine === 'owner' || mine === 'officer';

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['crew', tag] });
    void queryClient.invalidateQueries({ queryKey: ['crews'] });
    void useSession.getState().refresh();
  };

  const act: Act = async (path, body, note, method = 'POST') => {
    setBusy(true);
    try {
      if (method === 'DELETE') await api.del(path);
      else await api.post(path, body ?? {});
      refresh();
      if (note) toast(note, 'good');
      return true;
    } catch (err) {
      toast((err as Error).message, 'bad');
      return false;
    } finally {
      setBusy(false);
    }
  };

  async function leave() {
    const ok = await confirm(
      'Leave ' + crew.name + '?',
      mine === 'owner'
        ? 'You are the host. Make someone else host first from their member menu, or the crew may close when you go.'
        : crew.settings.join === 'open' ? 'You can join again at any time.' : 'You would have to be let back in.',
      { confirmLabel: 'Leave crew', danger: true }
    );
    if (ok) await act('/api/crews/' + crew.id + '/leave', {}, 'You left ' + crew.name);
  }

  const moreItems: MenuItem[] = [];
  if (canManage) {
    moreItems.push({ label: 'Crew settings', icon: 'sliders', onClick: () => setSettings(true) });
    moreItems.push({ label: 'Plan an event', icon: 'calendar', onClick: () => setPlanning(true) });
    moreItems.push({ label: 'Announce something', icon: 'megaphone', onClick: () => setAnnouncing(true) });
    moreItems.push('-');
  }
  moreItems.push({ label: chatMode === 'minimized' || chatMode === 'closed' ? 'Open crew chat' : 'Minimize crew chat', icon: 'chat', onClick: () => setChatMode(chatMode === 'minimized' || chatMode === 'closed' ? 'open' : 'minimized') });
  moreItems.push({ label: 'Copy crew tag', icon: 'copy', onClick: () => { void navigator.clipboard?.writeText(crew.tag); toast('Tag copied', 'good'); } });
  moreItems.push({ label: 'Discover other crews', icon: 'search', onClick: () => go('crews?find=1') });
  moreItems.push('-');
  moreItems.push({ label: 'Report crew', icon: 'flag', onClick: () => setReporting(true) });
  if (mine) moreItems.push({ label: 'Leave crew', icon: 'close', danger: true, onClick: () => void leave() });

  const members = useMemo(
    () => [...crew.members].sort((a, b) => roleRank(a) - roleRank(b) || statusRank(a.user) - statusRank(b.user)),
    [crew.members]
  );
  const online = crew.members.filter((m) => statusRank(m.user) === 0).length;

  return (
    <>
      {/* Sized by its own width, not the window's: with chat expanded beside
          it the page lays itself out for the room it really has, and steps
          aside altogether when chat fills the window. */}
      <div
        aria-hidden={covering || undefined}
        className={cn(
          '@container min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto transition-opacity duration-[280ms]',
          covering && 'pointer-events-none w-0 flex-none opacity-0'
        )}
      >
        <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-4 px-4 py-4 @xl:px-6 animate-[fade-in_280ms_ease-out] motion-reduce:animate-none">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[13px] text-mute">
            <button
              type="button"
              onClick={() => go('crews?find=1')}
              className="rounded-[6px] px-0.5 outline-none hover:text-ink hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              Crews
            </button>
            <span aria-hidden className="text-ghost">/</span>
            <span className="truncate font-semibold text-dim">{crew.name}</span>
          </nav>

          {/* ---- the cover ---- */}
          <section aria-label={crew.name} className="smooth-clip relative isolate overflow-hidden rounded-[calc(22px*var(--radius-mul,1))]">
            <CrewBanner appearance={crew.appearance} className="absolute inset-0 -z-10 h-full w-full" />
            {/* Darker toward the bottom left, where the name sits, so it reads
                on any picture without a veil over the whole thing. */}
            <span aria-hidden className="absolute inset-0 -z-10 bg-[linear-gradient(to_top_right,rgba(10,8,14,.82),rgba(10,8,14,.35)_55%,rgba(10,8,14,.1))]" />

            <span aria-hidden className="pointer-events-none absolute inset-0 z-[1] rounded-[inherit] shadow-[inset_0_0_0_1px_var(--line-soft)]" />
            <div className="flex min-h-[188px] flex-wrap items-end gap-4 p-5 text-white @xl:p-6">
              <span className="flex-none rounded-[calc(22px*var(--radius-mul,1))] bg-white/12 p-[3px] shadow-[0_12px_30px_-12px_rgba(0,0,0,.65)]">
                <CrewMark tag={crew.tag} appearance={crew.appearance} size={88} className="rounded-[calc(18px*var(--radius-mul,1))]" />
              </span>
              <div className="min-w-[200px] flex-1">
                <h1 className="m-0 truncate text-[28px] leading-tight font-extrabold tracking-[-.02em] uppercase @xl:text-[34px]">{crew.name}</h1>
                <p className="mt-1 mb-0 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[15px] text-white/85">
                  {/* The chip itself rather than the letters: this is where
                      the crew's own style belongs most, and it is the same
                      chip its members wear everywhere else. */}
                  <CrewTag crew={crew.chip} size="md" still />
                  <span aria-hidden>·</span>
                  <span>{crew.memberCount} {crew.memberCount === 1 ? 'member' : 'members'}</span>
                  <span aria-hidden>·</span>
                  <span className="flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full bg-good" />{online} online</span>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
                {mine ? (
                  <button
                    type="button"
                    onClick={() => setInviting(true)}
                    className="flex h-10 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent px-4 text-[14px] font-semibold text-accent-fg shadow-[0_8px_20px_-8px_color-mix(in_srgb,var(--accent)_80%,transparent)] outline-none transition-[background-color,transform] duration-[250ms] hover:bg-accent-hi active:scale-[.98] focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]"
                  >
                    <Icon name="addFriend" size={17} />
                    Invite member
                  </button>
                ) : crew.invited ? (
                  <Button kind="primary" disabled={busy} onClick={() => void act('/api/crews/' + crew.id + '/join', {}, 'Welcome to ' + crew.name)}>Accept invite</Button>
                ) : (
                  <Button
                    kind="primary"
                    disabled={busy || crew.settings.join === 'invite'}
                    onClick={() => void act('/api/crews/' + crew.id + '/join', {}, crew.settings.join === 'open' ? 'Welcome to ' + crew.name : 'Request sent')}
                  >
                    {crew.settings.join === 'open' ? 'Join crew' : crew.settings.join === 'request' ? 'Ask to join' : 'Invite only'}
                  </Button>
                )}
                {/* Chat put away (minimized or closed): the way back, with its unread count. */}
                {chatMode === 'minimized' || chatMode === 'closed' ? (
                  <button
                    type="button"
                    onClick={() => setChatMode('open')}
                    className="flex h-10 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-black/35 px-3.5 text-[14px] font-semibold text-white outline-none backdrop-blur-sm transition-colors duration-[250ms] hover:bg-black/50 focus-visible:shadow-[0_0_0_3px_var(--accent)] animate-[fade-in_250ms_ease-out]"
                  >
                    <Icon name="chat" size={17} />
                    Crew chat
                    {unread ? <span className="grid h-5 min-w-5 place-items-center rounded-full bg-bad px-1 text-[11.5px] font-bold tabular-nums">{unread > 99 ? '99+' : unread}</span> : null}
                  </button>
                ) : null}
                <Tooltip content="What this crew is about">
                  <button
                    type="button"
                    onClick={() => setAbout(true)}
                    className="flex h-10 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-black/35 px-3.5 text-[14px] font-semibold text-white outline-none backdrop-blur-sm transition-colors duration-[250ms] hover:bg-black/50 focus-visible:shadow-[0_0_0_3px_var(--accent)]"
                  >
                    <Icon name="info" size={17} />
                    About
                  </button>
                </Tooltip>
                <Tooltip content="More crew actions">
                  <button
                    type="button"
                    aria-label="More crew actions"
                    aria-haspopup="menu"
                    onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMoreAt({ x: Math.max(8, r.right - 214), y: r.bottom + 4 }); }}
                    className="grid h-10 w-10 place-items-center rounded-[calc(12px*var(--radius-mul,1))] bg-black/35 text-white outline-none backdrop-blur-sm transition-colors duration-[var(--t-hover)] hover:bg-black/50 focus-visible:shadow-[0_0_0_3px_var(--accent)]"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
                  </button>
                </Tooltip>
              </div>
            </div>
          </section>

          {/* ---- tabs ---- */}
          <div
            role="tablist"
            aria-label="Crew sections"
            className="flex items-center gap-6 border-b border-line-soft px-1"
            onKeyDown={(e) => {
              if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
              e.preventDefault();
              const order: Tab[] = ['overview', 'members', 'events'];
              const next = order[(order.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : 2)) % 3]!;
              setTab(next);
              document.getElementById('crew-tab-' + next)?.focus();
            }}
          >
            {([['overview', 'Overview', null], ['members', 'Members', crew.memberCount], ['events', 'Events', crew.events.length || null]] as const).map(([id, label, count]) => {
              const on = tab === id;
              return (
                <button
                  key={id}
                  id={'crew-tab-' + id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  aria-controls="crew-panel"
                  tabIndex={on ? 0 : -1}
                  onClick={() => setTab(id)}
                  className={cn(
                    'relative flex h-11 items-center gap-2 text-[15px] font-semibold outline-none',
                    'transition-colors duration-[var(--t-hover)] ease-[var(--ease)] focus-visible:shadow-[inset_0_0_0_2px_var(--accent)]',
                    on ? 'text-accent' : 'text-dim hover:text-ink'
                  )}
                >
                  {label}
                  {count != null ? <span className="rounded-full bg-surface-3 px-2 py-px text-[12.5px] font-semibold text-dim tabular-nums">{count}</span> : null}
                  <span aria-hidden className={cn('absolute inset-x-0 -bottom-px h-[3px] rounded-full bg-accent transition-opacity duration-[250ms]', on ? 'opacity-100' : 'opacity-0')} />
                </button>
              );
            })}

            {/* Settings sits on the same line as the sections, not buried in
                a three-dot menu: for whoever runs the crew it is the control
                they reach for most. */}
            {canManage ? (
              <>
                <span className="flex-1" />
                <button
                  type="button"
                  onClick={() => setSettings(true)}
                  className="mb-1.5 flex h-9 flex-none items-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 px-3 text-[13.5px] font-semibold text-dim outline-none transition-colors hover:bg-surface-3 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
                >
                  <Icon name="sliders" size={15} />
                  Settings
                </button>
              </>
            ) : null}
          </div>

          <div id="crew-panel" role="tabpanel" aria-labelledby={'crew-tab-' + tab}>
            {tab === 'overview' ? (
              <div className="grid gap-4 @3xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
                <div className="flex min-w-0 flex-col gap-4">
                  {canManage && crew.requests.length ? <Requests crew={crew} busy={busy} act={act} /> : null}
                  <section aria-labelledby="crew-members-title" className={cn(CARD, 'p-4')}>
                    <div className="mb-2 flex items-center gap-2">
                      <h2 id="crew-members-title" className="m-0 text-[17px] font-bold text-ink">Members</h2>
                      <span className="rounded-full bg-surface-3 px-2 py-px text-[12.5px] font-semibold text-dim tabular-nums">{crew.memberCount}</span>
                      <span className="flex-1" />
                      {crew.members.length > 5 ? (
                        <button type="button" onClick={() => setTab('members')} className="rounded-[6px] px-1 text-[14px] font-semibold text-accent outline-none hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]">View all</button>
                      ) : null}
                    </div>
                    <MemberList crew={crew} members={members.slice(0, 5)} busy={busy} act={act} confirm={confirm} onInviteToParty={setPartyInvite} />
                  </section>
                </div>
                <div className="flex min-w-0 flex-col gap-4">
                  <VoiceLounge crew={crew} />
                  <EventsBlock crew={crew} act={act} onPlan={() => setPlanning(true)} limit={1} />
                  <CrewLevel crew={crew} />
                </div>
              </div>
            ) : tab === 'members' ? (
              <section aria-label="All members" className={cn(CARD, 'p-4')}>
                {canManage && crew.requests.length ? <div className="mb-4"><Requests crew={crew} busy={busy} act={act} bare /></div> : null}
                <MemberList crew={crew} members={members} busy={busy} act={act} confirm={confirm} onInviteToParty={setPartyInvite} />
              </section>
            ) : (
              <div className="flex flex-col gap-4">
                <EventsBlock crew={crew} act={act} onPlan={() => setPlanning(true)} />
                {/* Everything that happens later, in one place rather than
                    scattered down the overview. */}
                <WhenFree crew={crew} canManage={canManage} />
                <Challenges crew={crew} canManage={canManage} />
                <Tournaments crew={crew} canManage={canManage} />
                <CrewHistory crew={crew} canManage={canManage} onAgain={(e) => setAgain(e)} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Crew chat, docked beside the page - one conversation in three sizes. */}
      <CrewChat crew={crew} />

      {/* The sidebar's red dot has a twin here for when the launcher is off
          screen on a small window: the tab title says it too. */}
      <UnreadTitle count={chatMode === 'minimized' || chatMode === 'closed' ? unread : 0} />

      {moreAt ? <MenuPanel at={moreAt} items={moreItems} onClose={() => setMoreAt(null)} /> : null}
      {about ? (
        <Modal title={crew.name} subtitle="What this crew is about" onClose={() => setAbout(false)}>
          <div className="flex flex-col gap-4">
            <CrewWelcome crew={crew} />
            <AboutCrew crew={crew} />
          </div>
        </Modal>
      ) : null}

      {settings ? <CrewWorkspace crew={crew} onClose={() => setSettings(false)} onChanged={refresh} /> : null}
      {reporting ? <ReportCrew crew={crew} onClose={() => setReporting(false)} /> : null}
      {inviting ? <InviteMember crew={crew} onClose={() => setInviting(false)} /> : null}
      {announcing ? <Announce crew={crew} onClose={() => setAnnouncing(false)} /> : null}
      {planning || again ? (
        <PlanEvent
          crew={crew}
          again={again ? { title: again.title, gameId: again.gameId, description: again.description, durationMin: again.durationMin, repeat: again.repeat } : undefined}
          onClose={() => { setPlanning(false); setAgain(null); }}
          onDone={refresh}
        />
      ) : null}
      {partyInvite ? <InviteToParty user={partyInvite} onClose={() => setPartyInvite(null)} /> : null}
      {dialog}
    </>
  );
}

/** Puts the unread count in the window title while chat is minimized. */
function UnreadTitle({ count }: { count: number }) {
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s*/, '');
    document.title = count ? '(' + (count > 99 ? '99+' : count) + ') ' + base : base;
    return () => { document.title = document.title.replace(/^\(\d+\+?\)\s*/, ''); };
  }, [count]);
  return null;
}

function roleRank(m: CrewMember) { return m.role === 'owner' ? 0 : m.role === 'officer' ? 1 : 2; }
function statusRank(u: PublicUser) { return u.status === 'offline' || u.status === 'invisible' ? 1 : 0; }

function presence(u: PublicUser): { dot: string; label: string } {
  if (u.status === 'dnd') return { dot: 'bg-bad', label: 'Do not disturb' };
  if (u.status === 'idle') return { dot: 'bg-warn', label: 'Away' };
  if (u.status === 'offline' || u.status === 'invisible') return { dot: 'bg-ghost', label: 'Offline' };
  return { dot: 'bg-good', label: 'Online' };
}

/* ------------------------------------------------------------ members */

/**
 * Compact member rows, each with the one shared set of actions.
 *
 * The avatar and the name open the compact profile (the shared behaviour).
 * The options button, right-click on the row and the keyboard context-menu
 * command all open the same menu; opening, choosing and dismissing it never
 * reach the profile underneath.
 */
function MemberList({ crew, members, busy, act, confirm, onInviteToParty }: {
  crew: Crew;
  members: CrewMember[];
  busy: boolean;
  act: Act;
  confirm: (t: string, m: string, o?: { confirmLabel?: string; danger?: boolean }) => Promise<boolean>;
  onInviteToParty: (u: PublicUser) => void;
}) {
  const catalog = useSession((s) => s.catalog);
  const me = useSession((s) => s.me);
  const [menu, setMenu] = useState<{ at: { x: number; y: number }; items: MenuItem[] } | null>(null);

  if (crew.membersHidden) {
    return <p className="m-0 py-3 text-[14px] text-mute">This crew keeps its member list to members only.</p>;
  }

  const mine = crew.myRole;

  function itemsFor(m: CrewMember): MenuItem[] {
    const self = m.user.id === me?.id;
    const items: MenuItem[] = [
      { label: self ? 'View your profile' : 'View profile', icon: 'profile', onClick: () => go('u/' + m.user.username) }
    ];
    if (self) {
      items.push({ label: 'Edit profile', icon: 'edit', onClick: () => go('settings/profile') });
      return items;
    }
    items.push({ label: 'Message', icon: 'chat', onClick: () => go('chat/' + m.user.username) });
    items.push({ label: 'Invite to a party', icon: 'squads', onClick: () => onInviteToParty(m.user) });

    if (m.role === 'owner') return items;
    const base = '/api/crews/' + crew.id + '/members/' + m.user.id;
    if (mine === 'owner') {
      items.push('-');
      items.push({
        label: m.role === 'officer' ? 'Make a member again' : 'Make co-host',
        icon: 'crown',
        onClick: () => void act(base, { role: m.role === 'officer' ? 'member' : 'officer' }, m.role === 'officer' ? m.user.displayName + ' is a member again' : m.user.displayName + ' is now a co-host')
      });
      items.push({
        label: 'Transfer ownership',
        icon: 'key',
        onClick: () => void (async () => {
          if (await confirm('Make ' + m.user.displayName + ' the host of ' + crew.name + '?', 'They become the host and you become a co-host. Only they can hand it back.', { confirmLabel: 'Transfer ownership', danger: true })) {
            await act('/api/crews/' + crew.id + '/owner', { userId: m.user.id }, m.user.displayName + ' is now the host');
          }
        })()
      });
    }
    if (mine === 'owner' || (mine === 'officer' && m.role === 'member')) {
      items.push('-');
      items.push({
        label: 'Remove from crew',
        icon: 'close',
        danger: true,
        onClick: () => void (async () => {
          if (await confirm('Remove ' + m.user.displayName + ' from ' + crew.name + '?', 'They lose access to crew chat and the voice lounge straight away. They can ask to join again.', { confirmLabel: 'Remove', danger: true })) {
            await act(base, { remove: true }, 'Removed ' + m.user.displayName);
          }
        })()
      });
    }
    return items;
  }

  const openAt = (m: CrewMember, x: number, y: number) => setMenu({ at: { x, y }, items: itemsFor(m) });

  /* A crew can hold a hundred people and every row is an avatar, a presence
     dot and a menu waiting to be opened. Ten, and a button for the rest. */
  /* Keyed on the crew, not on how many are in it: somebody joining while you
     have the whole list open should not fold it back to ten. */
  const paged = usePaged(members, crew.id);

  return (
    <>
      <ul className="m-0 flex list-none flex-col p-0">
        {paged.slice.map((m) => {
          const p = presence(m.user);
          const gameId = m.user.playing ?? m.user.games?.[0]?.id ?? null;
          const game = gameId ? catalog?.games.find((g) => g.id === gameId) : null;
          const playing = !!m.user.playing;
          return (
            <li
              key={m.user.id}
              tabIndex={0}
              aria-label={m.user.displayName + ', ' + ROLE_LABEL[m.role] + ', ' + p.label + '. Press Shift F10 for actions.'}
              onContextMenu={(e) => {
                /* Right-click on the row opens the member menu - but not on
                   one of the row's own controls, which keep their own. */
                if ((e.target as HTMLElement).closest('button, a, [role="button"]') && !(e.target as HTMLElement).closest('[data-row-trigger]')) return;
                e.preventDefault();
                openAt(m, e.clientX, e.clientY);
              }}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                  e.preventDefault();
                  const r = e.currentTarget.getBoundingClientRect();
                  openAt(m, Math.max(8, r.right - 230), r.top + 12);
                }
              }}
              className="group flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] px-2 py-2 outline-none transition-[background-color] duration-[250ms] hover:bg-surface-2 focus-visible:shadow-[inset_0_0_0_2px_var(--accent)]"
            >
              <button
                type="button"
                data-row-trigger
                onClick={(e) => openUserCard(m.user, e.currentTarget)}
                aria-label={'Show ' + m.user.displayName + "'s profile"}
                className="relative flex-none rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--bg-1),0_0_0_4px_var(--accent)]"
              >
                <Avatar user={m.user} size={44} />
                <span aria-hidden className={cn('absolute right-0 bottom-0 h-3 w-3 rounded-full ring-2 ring-[var(--bg-1)]', p.dot)} />
              </button>
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-2">
                  <span data-row-trigger className="min-w-0 truncate"><UserName user={m.user} className="text-[15px] font-bold" /></span>
                  {m.role !== 'member' ? (
                    <span className="inline-flex flex-none items-center gap-1 rounded-full bg-[color-mix(in_srgb,#f5b83d_16%,transparent)] px-2 py-px text-[12px] font-semibold text-[color-mix(in_srgb,#f5b83d_75%,var(--ink))]">
                      {m.role === 'owner'
                        ? <svg width="12" height="12" viewBox="0 0 24 24" fill="#f5b83d" aria-hidden="true"><path d="M3.4 7.6 7 12l5-6.6L17 12l3.6-4.4-1.4 11H4.8Z" /></svg>
                        : <svg width="12" height="12" viewBox="0 0 24 24" fill="#f5b83d" aria-hidden="true"><path d="m12 2.8 2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4L2.8 9.5l6.4-.8Z" /></svg>}
                      {ROLE_LABEL[m.role]}
                    </span>
                  ) : null}
                </span>
                <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px] text-mute">
                  <span className="flex items-center gap-1.5"><span aria-hidden className={cn('h-2 w-2 rounded-full', p.dot)} />{p.label}</span>
                  <Tooltip content={playing && game ? 'Playing ' + game.name + ' now' : game ? 'Main game' : 'Their own level'}>
                    <span tabIndex={-1} className="flex min-w-0 items-center gap-1.5">
                      <Icon name="dice" size={14} className="flex-none" />
                      <span className="truncate">{game ? (playing ? 'Playing ' : '') + game.name : 'Level ' + m.user.level}</span>
                    </span>
                  </Tooltip>
                </span>
              </span>
              <IconButton
                label={'Actions for ' + m.user.displayName}
                disabled={busy}
                className="h-9 w-9 flex-none rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft"
                onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); openAt(m, Math.max(8, r.right - 230), r.bottom + 4); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
              </IconButton>
            </li>
          );
        })}
      </ul>
      <ShowMore rest={paged.rest} next={paged.next} noun="member" onMore={paged.more} onAll={paged.all} className="pt-2" />
      {menu ? <MenuPanel at={menu.at} items={menu.items} onClose={() => setMenu(null)} /> : null}
    </>
  );
}

function Requests({ crew, busy, act, bare }: { crew: Crew; busy: boolean; act: Act; bare?: boolean }) {
  const body = (
    <>
      <h2 id="crew-requests-title" className="m-0 mb-3 text-[17px] font-bold text-ink">
        Wants to join <span className="font-semibold text-mute">({crew.requests.length})</span>
      </h2>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {crew.requests.map((r) => (
          <li key={r.user.id} className="flex flex-wrap items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 px-3 py-2.5">
            <Avatar user={r.user} size={40} presence />
            <span className="min-w-0 flex-1">
              <UserName user={r.user} className="block truncate text-[15px] font-bold" />
              <span className="block text-[13px] text-mute">Level {r.user.level}</span>
            </span>
            <span className="flex gap-2">
              <Button kind="primary" size="sm" disabled={busy} onClick={() => void act('/api/crews/' + crew.id + '/requests/' + r.user.id, { accept: true }, r.user.displayName + ' joined the crew')}>Accept</Button>
              <Button kind="outline" size="sm" disabled={busy} onClick={() => void act('/api/crews/' + crew.id + '/requests/' + r.user.id, { accept: false }, 'Request declined')}>Decline</Button>
            </span>
          </li>
        ))}
      </ul>
    </>
  );
  if (bare) return <div role="region" aria-labelledby="crew-requests-title">{body}</div>;
  return <section aria-labelledby="crew-requests-title" className={cn(CARD, 'p-4')}>{body}</section>;
}

/* ------------------------------------------------------------ events */

function EventsBlock({ crew, act, onPlan, limit }: { crew: Crew; act: Act; onPlan: () => void; limit?: number }) {
  const canManage = crew.myRole === 'owner' || crew.myRole === 'officer';
  const list = limit ? crew.events.slice(0, limit) : crew.events;
  return (
    <section aria-labelledby="crew-events-title" className={cn(CARD, 'p-4')}>
      <div className="mb-2 flex items-center gap-2">
        <h2 id="crew-events-title" className="m-0 text-[17px] font-bold text-ink">{limit ? 'Next event' : 'Upcoming events'}</h2>
        <span className="flex-1" />
        {canManage ? <Button kind="outline" size="sm" icon={<Icon name="plus" size={14} />} onClick={onPlan}>Plan</Button> : null}
      </div>
      {list.length ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {list.map((ev) => <EventRow key={ev.id} event={ev} crew={crew} act={act} />)}
        </ul>
      ) : (
        <div className="flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 px-3 py-3">
          <span className="grid h-10 w-10 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] bg-surface-3 text-dim"><Icon name="calendar" size={18} /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-[14.5px] font-semibold text-ink">No events planned</span>
            <span className="block text-[13px] text-mute">{canManage ? 'Plan the next game night - the whole crew is told.' : 'The host and co-hosts add events here.'}</span>
          </span>
        </div>
      )}
    </section>
  );
}

function EventRow({ event, crew, act }: { event: CrewEvent; crew: Crew; act: Act }) {
  const catalog = useSession((s) => s.catalog);
  const me = useSession((s) => s.me);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const d = new Date(event.startsAt);
  const game = event.gameId ? catalog?.games.find((g) => g.id === event.gameId) : null;
  const canCancel = crew.myRole === 'owner' || crew.myRole === 'officer' || event.createdBy?.id === me?.id;
  const end = new Date(event.endsAt ?? event.startsAt + 2 * 60 * 60 * 1000);
  const when = event.live
    ? 'Until ' + end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }) + ' · ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const items: MenuItem[] = [{ label: 'Copy details', icon: 'copy', onClick: () => void navigator.clipboard?.writeText(event.title + ' - ' + when) }];
  /* A game night that only exists in one app is one people miss. */
  items.push({
    label: 'Add to my calendar',
    icon: 'calendar',
    onClick: () => downloadIcs({
      id: event.id,
      title: event.title,
      description: [event.description, 'With ' + crew.name + ' on Pugo'].filter(Boolean).join('\n'),
      startsAt: event.startsAt,
      endsAt: event.endsAt ?? event.startsAt + 2 * 60 * 60 * 1000,
      where: game ? game.name : 'Pugo'
    })
  });
  /* The point of the evening: from the plan to an actual party, carrying the
     game and the name so nobody fills the form in again. */
  if (crew.myRole) {
    items.push({
      label: 'Start the party for this',
      icon: 'squads',
      onClick: () => go('parties?create=1&game=' + (event.gameId ?? '') + '&title=' + encodeURIComponent(event.title))
    });
  }
  if (canCancel) items.push('-', { label: 'Cancel event', icon: 'trash', danger: true, onClick: () => void act('/api/crews/' + crew.id + '/events/' + event.id, undefined, 'Event cancelled', 'DELETE') });

  return (
    <li className="flex flex-wrap items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 px-3 py-3">
      <span className="flex w-12 flex-none flex-col items-center rounded-[calc(10px*var(--radius-mul,1))] bg-surface-1 py-1.5 text-center">
        <span className="text-[11px] font-semibold text-bad uppercase">{d.toLocaleDateString([], { month: 'short' })}</span>
        <span className="text-[18px] leading-none font-extrabold text-ink tabular-nums">{d.getDate()}</span>
      </span>
      <span className="min-w-[160px] flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[15px] font-semibold text-ink">{event.title}</span>
          {event.live ? (
            <span className="flex flex-none items-center gap-1.5 rounded-full bg-[color-mix(in_srgb,var(--bad)_16%,transparent)] px-2 py-px text-[12px] font-bold text-bad animate-[fade-in_280ms_ease-out]">
              <span aria-hidden className="relative flex h-2 w-2">
                <span className="absolute inset-0 animate-ping rounded-full bg-bad opacity-60 motion-reduce:animate-none" />
                <span className="relative h-2 w-2 rounded-full bg-bad" />
              </span>
              Live now
            </span>
          ) : null}
        </span>
        <span className="block truncate text-[13px] text-mute">
          {when}{game ? ' · ' + game.name : ''}
          {event.repeat ? ' · ' + (event.repeat === 'weekly' ? 'every week' : 'every fortnight') : ''} · {event.goingCount} going
          {event.maybeCount ? ' · ' + event.maybeCount + ' maybe' : ''}
        </span>
      </span>
      {crew.myRole ? <Rsvp crewId={crew.id} event={event} /> : null}
      <IconButton label={'More for ' + event.title} className="h-9 w-9" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: Math.max(8, r.right - 214), y: r.bottom + 4 }); }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
      </IconButton>
      {menu ? <MenuPanel at={menu} items={items} onClose={() => setMenu(null)} /> : null}
    </li>
  );
}

function PlanEvent({ crew, onClose, onDone, again }: {
  crew: Crew;
  onClose: () => void;
  onDone: () => void;
  /** An evening that already happened, being run again. */
  again?: { title: string; gameId: string | null; description: string; durationMin: number; repeat: string | null };
}) {
  const catalog = useSession((s) => s.catalog);
  const toast = useToast();
  const [title, setTitle] = useState(again?.title ?? '');
  const [gameId, setGameId] = useState(again?.gameId ?? '');
  const [description, setDescription] = useState(again?.description ?? '');
  const [duration, setDuration] = useState(String(again?.durationMin ?? 120));
  const [repeat, setRepeat] = useState<string>(again?.repeat ?? '');
  const [when, setWhen] = useState(() => {
    const d = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
    d.setHours(19, 0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!title.trim()) { setError('Give the event a name.'); return; }
    const startsAt = new Date(when).getTime();
    if (!Number.isFinite(startsAt)) { setError('Pick a date and time.'); return; }
    setBusy(true);
    setError('');
    try {
      await api.post('/api/crews/' + crew.id + '/events', { title, description, gameId: gameId || null, startsAt, durationMin: Number(duration), repeat: repeat || null });
      toast('Event added - the crew has been told', 'good');
      onDone();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Plan an event"
      subtitle={'Everyone in ' + crew.name + ' gets a notification.'}
      onClose={onClose}
      footer={<><Button kind="plain" onClick={onClose}>Cancel</Button><Button kind="primary" disabled={busy} onClick={() => void save()}>{busy ? 'Adding…' : 'Add event'}</Button></>}
    >
      <div className="flex flex-col gap-4">
        <Field label="Name">
          <Input autoFocus value={title} maxLength={60} onChange={(e) => { setTitle(e.target.value); setError(''); }} placeholder="Friday game night" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="When (your time)">
            <Input type="datetime-local" value={when} onChange={(e) => { setWhen(e.target.value); setError(''); }} />
          </Field>
          <Field label="Game">
            <Select value={gameId} onChange={setGameId} placeholder="Any game" options={[{ value: '', label: 'Any game' }, ...(catalog?.games ?? []).map((g) => ({ value: g.id, label: g.name }))]} />
          </Field>
        </div>
        <Field label="How long it runs" hint="It leaves the calendar when it ends; the crew is told when it starts.">
          <Select
            value={duration}
            onChange={setDuration}
            options={([['30', '30 minutes'], ['60', '1 hour'], ['90', '1½ hours'], ['120', '2 hours'], ['180', '3 hours'], ['240', '4 hours'], ['360', '6 hours']] as const).map(([value, label]) => ({ value, label }))}
          />
        </Field>
        {/*
          A repeating night is made one occurrence at a time, as the last one
          ends - so moving or cancelling one Friday is an ordinary edit and
          the rest of the series is untouched.
        */}
        <Field label="Repeat" hint="The next one is made as this one finishes.">
          <Select
            value={repeat}
            onChange={setRepeat}
            options={[{ value: '', label: 'Just once' }, { value: 'weekly', label: 'Every week' }, { value: 'fortnightly', label: 'Every fortnight' }]}
          />
        </Field>
        <Field label="Details" hint={description.length + ' / 300'}>
          <Textarea rows={3} maxLength={300} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Chill games, good vibes. Everyone's welcome!" />
        </Field>
        {error ? <p role="alert" className="m-0 text-[14px] text-bad">{error}</p> : null}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------ side cards */

function VoiceLounge({ crew }: { crew: Crew }) {
  const voice = useVoice();
  const toast = useToast();
  const room = crew.voiceRoom;
  const here = voice.partyId === room;
  const people = (voice.roomStates[room] ?? []).filter((m) => m.user);

  return (
    <section aria-labelledby="crew-voice-title" className={cn(CARD, 'p-4')}>
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 text-ink"><Icon name="headset" size={19} /></span>
        <span className="min-w-0 flex-1">
          <h2 id="crew-voice-title" className="m-0 text-[16px] font-bold text-ink">Voice lounge</h2>
          <p className="m-0 text-[13px] text-mute">The lounge · {people.length} connected</p>
        </span>
      </div>
      {people.length ? (
        <div className="mt-3 flex items-center">
          {people.slice(0, 5).map((m) => (
            <span key={m.userId} className={cn('-mr-2 rounded-full ring-2 ring-[var(--bg-1)]', m.speaking && !m.muted && 'ring-good')}>
              <Avatar user={m.user!} size={30} />
            </span>
          ))}
          {people.length > 5 ? <span className="ml-3 text-[13px] text-mute">+{people.length - 5}</span> : null}
        </div>
      ) : null}
      {here ? (
        <div className="mt-3 flex gap-2">
          <Button kind={voice.muted ? 'primary' : 'outline'} className="flex-1" aria-pressed={voice.muted} disabled={voice.listenOnly} onClick={() => voice.toggleMute()}>{voice.muted ? 'Unmute' : 'Mute'}</Button>
          <Button kind="danger" className="flex-1" onClick={() => void voice.leave()}>Leave voice</Button>
        </div>
      ) : (
        <Button
          kind="primary"
          disabled={!crew.myRole}
          icon={<Icon name="headset" size={17} />}
          className="mt-3 w-full"
          onClick={() => void voice.join(room).catch((err: Error) => toast(err.message || 'Could not join the voice lounge', 'bad'))}
        >
          {crew.myRole ? 'Join voice' : 'Members only'}
        </Button>
      )}
    </section>
  );
}

function CrewLevel({ crew }: { crew: Crew }) {
  const colour = crewColor(crew.appearance);
  const toGo = Math.max(0, crew.level.need - crew.level.into);
  const pct = crew.level.need ? Math.min(100, Math.round((crew.level.into / crew.level.need) * 100)) : 100;
  return (
    <section aria-labelledby="crew-level-title" className={cn(CARD, 'p-4')}>
      <h2 id="crew-level-title" className="m-0 mb-3 text-[16px] font-bold text-ink">Crew level</h2>
      <div className="flex items-center gap-3">
        <span className="grid h-12 w-12 flex-none place-items-center rounded-[calc(14px*var(--radius-mul,1))] text-[20px] font-extrabold text-white" style={{ background: 'linear-gradient(145deg, ' + colour + ', color-mix(in srgb, ' + colour + ' 55%, #000))' }}>
          {crew.level.level}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2 text-[13px] text-mute">
            <Tooltip content="A crew levels up from the combined level of everyone in it">
              <span tabIndex={0} className="rounded outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]">{crew.score.levels} member levels</span>
            </Tooltip>
            <span className="tabular-nums">{toGo} to level {crew.level.level + 1}</span>
          </span>
          <span className="mt-2 block h-2 overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-label={'Progress to crew level ' + (crew.level.level + 1)} aria-valuemin={0} aria-valuemax={crew.level.need} aria-valuenow={crew.level.into}>
            <span className="block h-full rounded-full transition-[width] duration-500" style={{ width: pct + '%', background: colour }} />
          </span>
        </span>
      </div>
    </section>
  );
}

function AboutCrew({ crew }: { crew: Crew }) {
  const catalog = useSession((s) => s.catalog);
  const [joinTitle, joinNote] = JOIN_TEXT[crew.settings.join];
  const regionName = catalog?.regions.find((r) => r.id === crew.region)?.name ?? crew.region;
  const facts: [string, string, string][] = [
    ['globe', joinTitle, joinNote],
    ['chat', crew.language || 'English', 'Primary language'],
    ['pin', regionName || 'Anywhere', 'Primary region']
  ];
  return (
    <section aria-labelledby="crew-about-title" className={cn(CARD, 'p-4')}>
      <h2 id="crew-about-title" className="m-0 mb-3 text-[16px] font-bold text-ink">About</h2>
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {facts.map(([icon, title, note]) => (
          <li key={icon} className="flex gap-3">
            <Icon name={icon} size={20} className="mt-0.5 flex-none text-mute" />
            <span>
              <span className="block text-[14.5px] font-semibold text-ink">{title}</span>
              <span className="block text-[13px] text-mute">{note}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className={cn('mt-4 mb-0 border-t border-line-soft pt-3 text-[14.5px] leading-relaxed', crew.description ? 'text-dim' : 'text-mute')}>
        {crew.description || 'No description yet.'}
      </p>
      {crew.tags.length ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {crew.tags.map((t) => <span key={t} className="rounded-full bg-surface-2 px-2.5 py-1 text-[13px] font-medium text-dim">{t}</span>)}
        </div>
      ) : null}

    </section>
  );
}

/* ------------------------------------------------------------ dialogs */

function InviteMember({ crew, onClose }: { crew: Crew; onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });
  const memberIds = new Set(crew.members.map((m) => m.user.id));
  /* Online friends only: an invite to somebody who is not there goes unseen. */
  const candidates = (friends.data?.friends ?? [])
    .map((f) => f.user)
    .filter((u) => !memberIds.has(u.id) && !u.crew && u.status !== 'offline' && u.status !== 'invisible')
    .slice(0, 8);

  async function invite(username: string) {
    const clean = username.trim().replace(/^@/, '').toLowerCase();
    if (!clean) { setError('Type a username first.'); return; }
    setBusy(clean);
    setError('');
    try {
      await api.post('/api/crews/' + crew.id + '/invite', { username: clean });
      setSent((s) => [...s, clean]);
      setName('');
      toast('Invite sent to ' + clean, 'good');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Modal title={'Invite to ' + crew.name} subtitle="An invite lets them in straight away, whatever the join setting." onClose={onClose} footer={<Button kind="plain" onClick={onClose}>Done</Button>}>
      <form onSubmit={(e) => { e.preventDefault(); void invite(name); }} className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Field label="Username">
            <Input autoFocus value={name} onChange={(e) => { setName(e.target.value); setError(''); }} placeholder="username" spellCheck={false} aria-invalid={!!error} />
          </Field>
        </div>
        <Button kind="primary" type="submit" disabled={!!busy}>Invite</Button>
      </form>
      {error ? <p role="alert" className="mt-2 mb-0 text-[14px] text-bad">{error}</p> : null}

      {candidates.length ? (
        <>
          <h3 className="mt-5 mb-2 text-[15px] font-bold text-ink">Online friends not in a crew</h3>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {candidates.map((u) => {
              const done = sent.includes(u.username.toLowerCase());
              return (
                <li key={u.id} className="flex items-center gap-3 rounded-[var(--radius-l)] px-2 py-1.5">
                  <Avatar user={u} size={36} presence />
                  <UserName user={u} className="min-w-0 flex-1 truncate text-[15px] font-semibold" />
                  {done ? (
                    <span className="flex items-center gap-1 text-[14px] font-semibold text-good"><Icon name="check" size={15} />Invited</span>
                  ) : (
                    <Button kind="outline" size="sm" disabled={busy === u.username.toLowerCase()} onClick={() => void invite(u.username)}>Invite</Button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </Modal>
  );
}

function ReportCrew({ crew, onClose }: { crew: Crew; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  async function send() {
    if (!reason.trim()) { setError('Say what is wrong so staff know what to look at.'); return; }
    setBusy(true);
    setError('');
    try {
      await api.post('/api/crews/' + crew.id + '/report', { reason });
      toast('Reported. Staff will look.', 'good');
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={'Report ' + crew.name}
      subtitle="Reports go to staff, not to the crew."
      onClose={onClose}
      footer={<><Button kind="plain" onClick={onClose}>Cancel</Button><Button kind="primary" disabled={busy} onClick={() => void send()}>{busy ? 'Sending…' : 'Send report'}</Button></>}
    >
      <Field label="What is wrong with it?">
        <Textarea autoFocus value={reason} onChange={(e) => { setReason(e.target.value); setError(''); }} rows={4} maxLength={500} aria-invalid={!!error} />
      </Field>
      {error ? <p role="alert" className="mt-2 mb-0 text-[13.5px] text-bad">{error}</p> : null}
    </Modal>
  );
}

function CrewSkeleton() {
  return (
    <div className="min-h-0 flex-1 overflow-hidden" aria-busy="true" aria-label="Loading your crew">
      <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-4 px-4 py-4 sm:px-6">
        <div className="h-[188px] animate-pulse rounded-[calc(20px*var(--radius-mul,1))] bg-surface-3" />
        <div className="h-10 w-72 animate-pulse rounded bg-surface-3" />
        <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr]">
          <div className="h-[320px] animate-pulse rounded-[calc(18px*var(--radius-mul,1))] bg-surface-3" />
          <div className="h-[320px] animate-pulse rounded-[calc(18px*var(--radius-mul,1))] bg-surface-3" />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ no crew */

function NoCrew({ onFind, onMake }: { onFind: () => void; onMake: () => void }) {
  return (
    <>
      <div className="mb-5 overflow-hidden rounded-[calc(20px*var(--radius-mul,1))] border border-line-soft">
        <CrewBanner appearance={{ color: null, banner: null, bannerColor: null, icon: null, background: 'mesh', tagStyle: 'plate', tagFont: 'mono' }} className="h-[132px]">
          <div className="flex h-[132px] items-end p-5">
            <div>
              <h1 className="m-0 text-[28px] leading-tight font-extrabold tracking-[-.03em] text-ink">You are not in a crew</h1>
              <p className="mt-1 mb-0 text-[15px] text-dim">
                A crew wears its tag beside every member's name, has its own chat and voice lounge, and plans game nights.
              </p>
            </div>
          </div>
        </CrewBanner>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Choice icon="plus" title="Start a crew" note="Claim a tag, pick a colour, and invite the people you already play with." action="Create a crew" onClick={onMake} />
        <Choice icon="search" title="Discover crews" note="Browse every crew, ranked by the combined level of the people in them." action="Browse crews" onClick={onFind} />
      </div>
    </>
  );
}

function Choice({ icon, title, note, action, onClick }: {
  icon: string;
  title: string;
  note: string;
  action: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="pugo-panel group flex flex-col items-start rounded-[calc(18px*var(--radius-mul,1))] border border-line-soft bg-surface-1 p-5 text-left outline-none transition-[border-color,box-shadow] duration-[250ms] ease-[var(--ease)] hover:border-accent-line hover:shadow-[var(--shadow-1)] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
    >
      <span className="grid h-11 w-11 place-items-center rounded-[var(--radius-l)] bg-accent-soft text-accent">
        <Icon name={icon} size={20} />
      </span>
      <span className="mt-3 block text-[17px] font-bold text-ink">{title}</span>
      <span className="mt-1 block text-[14px] leading-relaxed text-mute">{note}</span>
      <span className="mt-3 inline-flex items-center gap-1 text-[14px] font-semibold text-accent">
        {action}
        <Icon name="arrowR" size={15} className="transition-transform duration-[250ms] group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}
